import { Box, ClickAwayListener, Grid, IconButton, InputAdornment, Menu, Paper, Typography, useMediaQuery } from '@mui/material';
import React, { lazy, memo, Suspense, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { currentActiveChatbotThreadTypeNew, sendCannedResponsePayload, sendTemplatePayload, uploadedMedia, getTemplateDetails } from '../../../../../models/axiosModel';
import whatsAppDefaultBgImg from "../../../../../assets/images/chatWidgetComponents/WhatsAppDefaultBg.png";
import { hexToRgba } from '../../../../../utils/constants';
import FileUploadInbox from './fileUploadInbox';
import ChatTextField, { ChatTextFieldHandle } from './chatTextField';
import styles from "../../../../../assets/styles/componentStyles/chatbotStyles/chats.module.scss";
import globalStyles from "../../../../../assets/styles/global.module.scss";
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import { useShallow } from 'zustand/react/shallow';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { isInteractiveTypeNameOrderDetails, WHATSAPP_TEMPLATE_STATUS } from '../../../../../components/dashboard/channels/global/whatsapp/templates/utils';
import GlobalPopUp from '../../../../../components/utility/globalPopUp';
import SelectTemplatePopup from './template/selectTemplatePopup';
import { failureBorderColor } from '../../../../../utils/colors';
import type { EmojiClickData } from 'emoji-picker-react';
import { getAgentName, getDynamicColorsBySource, getManualOverrideTextfieldDisabled, getThreadData, isChannelNameChronoxAiWebApp, isConversationHandleByOtherAgent, isLoggedInUserAssigned, isThreadArchived } from '../../../utils';
import { useRuleBasedInboxStore } from '../../../../../store/ruleBasedInboxStore';
import { UserContext, UserContextPayload } from '../../../../../context/userContext';
import { WhatsappTemplateContext, WhatsappTemplateContextPayload } from '../../../../../context/whatsappTemplateContext';
import agent from '../../../../../services/api';
import { isInstagramChannel } from '../../../channelConstants';

const LazyEmojiPicker = lazy(() => import('emoji-picker-react'));

const EMOJI_PICKER_FALLBACK = (
    <Box className={styles.emojiPickerFallback}>
        <Typography variant="caption" color="text.secondary">Loading emojis…</Typography>
    </Box>
);

interface ChatInputProps {
    handleSendMessageManually: (inputValue: string, flagMediaMessage: boolean, flagCannedResponseTemplate: boolean, flagTemplateMessage: boolean, selectedMediaSet: uploadedMedia[], sendTemplatePayload: sendTemplatePayload | null, cannedResponsePayload?: sendCannedResponsePayload) => Promise<void>,
    getManualOverrideConfig: (currentActiveChatbotThread: currentActiveChatbotThreadTypeNew | null) => string,
    getDynamicColorsByChatbotRouteSource: (source?: string) => ReturnType<typeof getDynamicColorsBySource>,
}

const ChatInput: React.FC<ChatInputProps> = (props) => {

    const { handleSendMessageManually, getManualOverrideConfig, getDynamicColorsByChatbotRouteSource } = props;

    const {
        newThreadInfo,
        checkIfChatIsExpiredForLeaveBtn,
        flagRenderNewConversation,
        listOfIntegration,
        currentActiveChatbotThread,
    } = useRuleBasedInboxStore(useShallow((state) => ({
        newThreadInfo: state.newThreadInfo,
        checkIfChatIsExpiredForLeaveBtn: state.checkIfChatIsExpiredForLeaveBtn,
        flagRenderNewConversation: state.flagRenderNewConversation,
        listOfIntegration: state.listOfIntegration,
        currentActiveChatbotThread: state.currentActiveChatbotThread,
    })));

    const { sortTemplateList } = useContext(WhatsappTemplateContext) as WhatsappTemplateContextPayload;
    const { setFlagLoading, handleError, userDetail } = useContext(UserContext) as UserContextPayload;

    const mobileScreen = useMediaQuery('(max-width: 600px)');

    const chatTextFieldRef = useRef<ChatTextFieldHandle>(null);
    const [emojiMenuAnchorEl, setEmojiMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [flagShowWindowExpireInfo, setFlagShowWindowExpireInfo] = useState<boolean>(false);
    const [flagSelectTemplatePopup, setFlagSelectTemplatePopup] = useState<boolean>(false);
    const [templateList, setTemplateList] = useState<getTemplateDetails[] | null>(null);

    const isEmojiMenuOpen = Boolean(emojiMenuAnchorEl);

    const dynamicColors = useMemo(
        () => getDynamicColorsByChatbotRouteSource(),
        [getDynamicColorsByChatbotRouteSource]
    );

    const isChatExpired = checkIfChatIsExpiredForLeaveBtn(getManualOverrideConfig(currentActiveChatbotThread), true);
    const isConversationHandleByOther = Boolean(currentActiveChatbotThread && isConversationHandleByOtherAgent(currentActiveChatbotThread, userDetail?.user_assignment_id) && !isLoggedInUserAssigned(userDetail, currentActiveChatbotThread));
    const isConversationArchived = isThreadArchived(currentActiveChatbotThread);
    const isChronoxAiWebApp = isChannelNameChronoxAiWebApp(currentActiveChatbotThread);
    const isManualOverrideTextfieldDisabled = getManualOverrideTextfieldDisabled(currentActiveChatbotThread, userDetail?.user_assignment_id);
    const isEmojiMenuDisabled = isManualOverrideTextfieldDisabled || isChatExpired || isChronoxAiWebApp;

    const inputFieldPlaceholder = useMemo(() => {
        const channelName = currentActiveChatbotThread?.chatbot_user_route_source;
        const isInstagram = isInstagramChannel(channelName);

        if (isChatExpired) {
            return isInstagram ? `24 hour window expired.` : `24 hour window expired. Only marketing messages can be sent now.`;
        }
        if (isConversationHandleByOther) {
            return `This conversation is currently being handled by ${currentActiveChatbotThread?.manual_agent_data?.map((agent) => getAgentName(agent))?.join(", ")?.replace(/, ([^,]*)$/, " and $1")}`;
        }
        if (isConversationArchived) {
            return 'This conversation is currently archived.';
        }
        if (flagRenderNewConversation) {
            return "Send a template message to initiate a conversation";
        }
        return "Type a message...";
    }, [isChatExpired, isConversationHandleByOther, isConversationArchived, flagRenderNewConversation, currentActiveChatbotThread?.manual_agent_data]);

    const showWhatsAppBg = Boolean(
        (currentActiveChatbotThread && currentActiveChatbotThread?.chatbot_user_route_source?.toLowerCase() == "whatsapp")
        || flagRenderNewConversation
    );

    const shouldShowStartAdornment = !isConversationHandleByOther && !isChatExpired && !isConversationArchived && !flagRenderNewConversation;
    const shouldShowEndAdornment = Boolean(
        (currentActiveChatbotThread && currentActiveChatbotThread?.channel_name?.toLowerCase() === "whatsapp" && !isConversationHandleByOther && !isConversationArchived)
        || flagRenderNewConversation
    );
    const shouldShowTemplateMessagePrompt = Boolean(
        mobileScreen && (flagRenderNewConversation || (currentActiveChatbotThread && isChatExpired))
    );
    const shouldShowSendBtn =
        !isConversationHandleByOther && !isChatExpired && !isConversationArchived && !flagRenderNewConversation;

    const handleOpenEmojiMenu = useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();
        event.preventDefault();
        setEmojiMenuAnchorEl(event.currentTarget);
    }, []);

    const handleCloseEmojiMenu = useCallback(() => {
        setEmojiMenuAnchorEl(null);
    }, []);

    const handleEmojiMenuClick = useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (isEmojiMenuDisabled) {
            event.preventDefault();
            return;
        }
        handleOpenEmojiMenu(event);
    }, [isEmojiMenuDisabled, handleOpenEmojiMenu]);

    const handleEmojiClick = useCallback((emojiData: EmojiClickData) => {
        chatTextFieldRef.current?.insertAtCursor(emojiData?.emoji);
    }, []);

    const handleShowWindowExpireInfo = useCallback(() => {
        setFlagShowWindowExpireInfo(true);
    }, []);

    const handleHideWindowExpireInfo = useCallback(() => {
        setFlagShowWindowExpireInfo(false);
    }, []);

    const handleCloseSelectTemplatePopup = useCallback(() => {
        setFlagSelectTemplatePopup(false);
    }, []);

    const filterApprovedTemplates = useCallback((templates: any[]) => {
        return templates?.filter((template: any) => (
            template?.whatsapp_template_metadata?.meta_whatsapp_template_status_name?.toLowerCase()
                === WHATSAPP_TEMPLATE_STATUS?.APPROVED?.toLowerCase()
            || template?.whatsapp_template_flag_interactive_message
        ));
    }, []);

    const getWATemplateList = useCallback(async (
        meta_integation_whatsapp_id: number,
        flagMarketingTemplateOnly?: boolean
    ) => {
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }));
        const res = await agent.getWhatsappTemplateList.get(meta_integation_whatsapp_id);
        if (res.status) {
            if (flagMarketingTemplateOnly) {
                const marketingTemplates = res.data.filter((template: any) => {
                    const nonInteractiveTemplate = !template?.whatsapp_template_flag_interactive_message;
                    if (flagRenderNewConversation) {
                        return nonInteractiveTemplate;
                    }
                    return nonInteractiveTemplate
                        && template.whatsapp_template_data?.[0]?.whatsapp_template_category_name?.toLowerCase() == "marketing";
                });
                const approvedMarketingTemplates = filterApprovedTemplates(marketingTemplates);
                setTemplateList(sortTemplateList(approvedMarketingTemplates));
            } else {
                const nextTemplateList = res.data?.filter(
                    (template: any) => !isInteractiveTypeNameOrderDetails(template?.interactive_type?.interactive_type_name)
                );
                const approvedTemplateList = filterApprovedTemplates(nextTemplateList);
                setTemplateList(sortTemplateList(approvedTemplateList));
            }
            setFlagSelectTemplatePopup(true);
        } else {
            handleError(res);
        }
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }));
    }, [flagRenderNewConversation, filterApprovedTemplates, sortTemplateList, setFlagLoading, handleError]);

    const handleOpenTemplatesClick = useCallback(() => {
        const metaIntegrationWhatsAppId = getThreadData(
            'metaIntegrationWhatsAppId',
            currentActiveChatbotThread,
            newThreadInfo,
            listOfIntegration
        ) as number;
        getWATemplateList(metaIntegrationWhatsAppId, !flagRenderNewConversation);
    }, [currentActiveChatbotThread, newThreadInfo, listOfIntegration, getWATemplateList, flagRenderNewConversation]);

    const textfieldWrapperStyle = useMemo(() => ({
        background: dynamicColors.send_msg_bg_color,
        border: `1px solid ${isChatExpired ? failureBorderColor : hexToRgba(dynamicColors.send_msg_icon_color)}`,
        boxShadow: `0 1px 1px ${isChatExpired ? failureBorderColor : hexToRgba(dynamicColors.send_msg_icon_color)}`,
    }), [dynamicColors.send_msg_bg_color, dynamicColors.send_msg_icon_color, isChatExpired]);

    const chatInputMainGridSx = useMemo(() => ({
        backgroundColor: dynamicColors.chatbot_bg_color,
        backgroundImage: showWhatsAppBg ? `url(${whatsAppDefaultBgImg})` : "none",
    }), [dynamicColors.chatbot_bg_color, showWhatsAppBg]);

    const textFieldSx = useMemo(() => ({
        backgroundColor: dynamicColors.send_msg_bg_color,
        '& .Mui-disabled': {
            'WebkitTextFillColor': `${dynamicColors.send_msg_font_color} !important`,
        },
        '& .MuiInputBase-input.Mui-disabled::placeholder': {
            color: isChatExpired ? 'red !important' : 'gray !important',
            WebkitTextFillColor: isChatExpired ? 'red' : 'gray',
            opacity: 1,
        },
        '& .MuiOutlinedInput-root': {
            padding: (isConversationHandleByOther || isConversationArchived)
                ? '9.5px 12px !important'
                : (isChatExpired || flagRenderNewConversation)
                    ? '6.5px 5px 6.5px 12px !important'
                    : '6.5px 5px !important',
        },
    }), [
        dynamicColors.send_msg_bg_color,
        dynamicColors.send_msg_font_color,
        isChatExpired,
        isConversationHandleByOther,
        isConversationArchived,
        flagRenderNewConversation,
    ]);

    // Stable adornments — created here so typing inside ChatTextField does not rebuild them
    const startAdornment = useMemo(() => (
        shouldShowStartAdornment ? (
            <InputAdornment position="start" className={styles.fileUploadIconInputAdornment}>
                <IconButton
                    onClick={handleEmojiMenuClick}
                    disabled={isEmojiMenuDisabled}
                    className={styles.addReactionIcon}
                >
                    <InsertEmoticonIcon />
                </IconButton>
            </InputAdornment>
        ) : null
    ), [shouldShowStartAdornment, handleEmojiMenuClick, isEmojiMenuDisabled]);

    const endAdornment = useMemo(() => (
        shouldShowEndAdornment ? (
            <FileUploadInbox
                getWATemplateList={getWATemplateList}
                handleSendMessageManually={handleSendMessageManually}
                getManualOverrideConfig={getManualOverrideConfig}
            />
        ) : null
    ), [
        shouldShowEndAdornment,
        currentActiveChatbotThread,
        getWATemplateList,
        handleSendMessageManually,
        getManualOverrideConfig,
    ]);

    return (
        <>
            <Grid item className={styles.chatInputMainGrid} sx={chatInputMainGridSx}>
                {shouldShowTemplateMessagePrompt ? (
                    <Grid item xs={12}>
                        <Grid
                            container
                            className={styles.sendTemplateGrid}
                            flexWrap="nowrap"
                            alignItems="flex-end"
                            gap={0.5}
                            sx={textfieldWrapperStyle}
                        >
                            <Grid item>
                                <Grid container gap={0.5} alignItems="center" flexWrap={"nowrap"}>
                                    {flagRenderNewConversation ? (
                                        <Typography className={styles.sendTemplateText}>
                                            Send template to initiate chat
                                        </Typography>
                                    ) : (
                                        <>
                                            <Typography className={styles.onlyMarketingMsgtext}>
                                                Only Marketing messages can be sent
                                            </Typography>
                                            <div className={`${globalStyles.relativePosition}`}>
                                                <Grid
                                                    item
                                                    onClick={handleShowWindowExpireInfo}
                                                    className={`${globalStyles.displayFlex}`}
                                                >
                                                    <InfoOutlinedIcon className={styles.windowExpireInfoIcon} />
                                                </Grid>
                                                {flagShowWindowExpireInfo && (
                                                    <ClickAwayListener onClickAway={handleHideWindowExpireInfo}>
                                                        <Paper elevation={3} className={styles.windowExpireInfo}>
                                                            <Typography
                                                                className={`${globalStyles.tertiaryFontSize} ${globalStyles.secondaryFontWeight}`}
                                                            >
                                                                {`24 hour window has expired`}
                                                            </Typography>
                                                        </Paper>
                                                    </ClickAwayListener>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </Grid>
                            </Grid>
                            <Grid item className={styles.templateChip} onClick={handleOpenTemplatesClick}>
                                Templates
                            </Grid>
                        </Grid>
                    </Grid>
                ) : (
                    <ChatTextField
                        ref={chatTextFieldRef}
                        handleSendMessageManually={handleSendMessageManually}
                        dynamicColors={dynamicColors}
                        textfieldWrapperStyle={textfieldWrapperStyle}
                        textFieldSx={textFieldSx}
                        isManualOverrideTextfieldDisabled={isManualOverrideTextfieldDisabled}
                        placeholder={inputFieldPlaceholder}
                        shouldShowSendBtn={shouldShowSendBtn}
                        startAdornment={startAdornment}
                        endAdornment={endAdornment}
                    />
                )}
            </Grid>

            {/* Emoji Menu — lives outside ChatTextField so typing does not re-render it */}
            <Menu
                anchorEl={emojiMenuAnchorEl}
                open={isEmojiMenuOpen}
                onClose={handleCloseEmojiMenu}
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                transitionDuration={0}
                className={styles.addReactionIconMenu}
                PopoverClasses={{
                    paper: styles.addReactionIconMenu,
                }}
            >
                {isEmojiMenuOpen && (
                    <Suspense fallback={EMOJI_PICKER_FALLBACK}>
                        <LazyEmojiPicker
                            onEmojiClick={handleEmojiClick}
                            width={280}
                            height={350}
                            searchPlaceholder="Search emoji..."
                            previewConfig={{
                                showPreview: false,
                            }}
                            skinTonesDisabled={true}
                        />
                    </Suspense>
                )}
            </Menu>

            {flagSelectTemplatePopup && (
                <GlobalPopUp
                    open={flagSelectTemplatePopup}
                    flagBackdrop
                    handleClose={handleCloseSelectTemplatePopup}
                    flagShowCloseIcon={true}
                    titlePadding="0px"
                    paperMinWidth="80%"
                    height="90%"
                >
                    <SelectTemplatePopup
                        templateList={templateList}
                        setFlagSelectTemplatePopup={setFlagSelectTemplatePopup}
                        handleSendMessageManually={handleSendMessageManually}
                    />
                </GlobalPopUp>
            )}
        </>
    );
};

export default memo(ChatInput);
